import DOMPurify from 'dompurify';
import { linkifyElement } from 'shared/helpers/linkifyHtml';

const limitCssValue = value => {
  if (!value) return value;
  return value.replace(
    /(\d+(?:\.\d+)?)(px|em|rem|%|pt|pc|in|cm|mm|ex|ch|vw|vh|vmin|vmax)/gi,
    (match, num, unit) => {
      const numValue = parseFloat(num);
      let maxValue;

      if (unit === 'px') maxValue = 800;
      else maxValue = unit === '%' ? 100 : 50;
      return numValue > maxValue ? `${maxValue}${unit}` : match;
    }
  );
};

const CSS_FORBIDDEN_PROPS = [
  'position',
  'z-index',
  'top',
  'left',
  'right',
  'bottom',
];
const CSS_LIMITED_PROPS = [
  'margin',
  'margin-left',
  'margin-right',
  'margin-top',
  'margin-bottom',
  'padding',
  'padding-left',
  'padding-right',
  'padding-top',
  'padding-bottom',
  'text-indent',
  'letter-spacing',
  'word-spacing',
  'font-size',
];

const isZero = value => parseFloat(value) === 0;

// Senders build vertical spacers from cells like
// <td height="20" style="font-size:0;line-height:0">&nbsp;</td>, so collapse
// them fully. The parsed value is compared, not the style text: a substring
// match on "font-size:0" would also catch "font-size:0.9375rem" and pile the
// cell's text lines on top of each other.
const collapseSpacerCell = el => {
  if (!['TD', 'TH'].includes(el.tagName)) return;
  const { fontSize, lineHeight } = el.style;
  if (!isZero(fontSize) && !isZero(lineHeight)) return;

  el.style.setProperty('font-size', '0');
  el.style.setProperty('line-height', '0');
  el.style.setProperty('padding', '0');
};

// Outlook builds list items as hanging indents, `margin-left:36pt` with
// `text-indent:-18pt`. Some webmails drop the margin and keep the indent, which
// pulls the bullet and the first letters of the line out of the bubble, where
// they get clipped. Give such elements the room the margin used to provide.
const keepHangingIndentInside = el => {
  const { textIndent, marginLeft, paddingLeft } = el.style;
  if (!(parseFloat(textIndent) < 0)) return;
  if (parseFloat(marginLeft) > 0 || parseFloat(paddingLeft) > 0) return;

  el.style.setProperty('padding-left', textIndent.trim().replace(/^-/, ''));
};

export const sanitizeHtmlContent = (html, { linkify = true } = {}) => {
  if (!html || typeof html !== 'string') return html;
  const nbsp = html.replace(/(&nbsp;){2,}/g, '&nbsp;');
  const sanitized = DOMPurify.sanitize(nbsp, {
    FORBID_TAGS: ['style', 'script', 'link', 'meta'],
    ADD_ATTR: [
      'width',
      'height',
      'cellspacing',
      'cellpadding',
      'border',
      'align',
      'valign',
      'bgcolor',
    ],
  });
  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  doc.querySelectorAll('[style]').forEach(el => {
    const originalStyle = el.getAttribute('style');

    CSS_FORBIDDEN_PROPS.forEach(prop => {
      el.style.removeProperty(prop);
    });
    CSS_LIMITED_PROPS.forEach(prop => {
      const value = el.style.getPropertyValue(prop);
      if (value) {
        el.style.setProperty(prop, limitCssValue(value));
      }
    });

    if (originalStyle && originalStyle.includes('!important')) {
      const importantProps = originalStyle
        .split(';')
        .filter(s => s.includes('!important'))
        .map(s => s.split(':')[0].trim());

      importantProps.forEach(prop => {
        const value = el.style.getPropertyValue(prop);
        if (value && !CSS_FORBIDDEN_PROPS.includes(prop)) {
          el.style.setProperty(prop, value, 'important');
        }
      });
    }

    collapseSpacerCell(el);
    keepHangingIndentInside(el);
  });
  // Emails often carry URLs the sender typed as plain text (contact form
  // fields, for example), which would otherwise render as unclickable text.
  if (linkify) linkifyElement(doc.body);
  return doc.body.innerHTML;
};

export default sanitizeHtmlContent;
